import {
  extractAxiosErrorMessage,
  PeopleCandidate,
  PeoplePromptRevision,
  PeopleRole,
  PromptRuleModel,
  useOperation,
  usePeopleDiscovery,
  usePeopleDiscoveryTrace,
  usePeopleRankingPrompt,
  usePeopleRankingPromptDiff,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";

// The result blob the test-run worker writes to the operation (dryRun path). The
// people worker returns the ranked shortlist inline for a test run so the panel
// can render it without persisting anything (operation_people_discovery.py).
interface TestRunResult {
  status?: string;
  candidates?: PeopleCandidate[];
  companyName?: string;
  apolloTotalEntries?: number;
  apolloCallCount?: number;
  sampled?: boolean;
  traceSubjectRef?: string;
  traceRunId?: string;
}

// The content fields an operator edits on a rule (improvement is operator-only).
interface RuleDraft {
  category: string;
  title: string;
  guidance: string;
  why: string;
  limits: string;
  evidence: string;
  improvement: string;
  status: "active" | "draft" | "retired";
}

const RULE_TEXT_FIELDS: { key: keyof RuleDraft; label: string; rows: number }[] =
  [
    { key: "guidance", label: "Guidance (the instruction - required)", rows: 3 },
    { key: "why", label: "Why (the generalisation behind it)", rows: 2 },
    { key: "limits", label: "Limits (where it stops applying)", rows: 2 },
    { key: "evidence", label: "Evidence (the cases behind it)", rows: 2 },
    {
      key: "improvement",
      label: "Improvement (operator-only - never sent to the model)",
      rows: 2,
    },
  ];

// Which band each ranked person falls into, mirrored from PeopleDiscoveryPanel so
// the test preview reads the same as the live find-people results.
const BAND_HEADINGS: { band: string; label: string }[] = [
  { band: "primary", label: "Primary targets" },
  { band: "secondary", label: "Also strong" },
  { band: "sustainability", label: "Sustainability / ESG" },
  { band: "other", label: "Other" },
];

const draftFromRule = (r: PromptRuleModel): RuleDraft => ({
  category: r.category,
  title: r.title,
  guidance: r.guidance,
  why: r.why,
  limits: r.limits,
  evidence: r.evidence,
  improvement: r.improvement,
  status: r.status,
});

const emptyDraft = (): RuleDraft => ({
  category: "",
  title: "",
  guidance: "",
  why: "",
  limits: "",
  evidence: "",
  improvement: "",
  status: "active",
});

const revisionLabel = (r: PeoplePromptRevision): string => {
  const who = r.isSeed ? "built-in default" : `saved by ${r.createdBy}`;
  const when = r.createdAt
    ? ` on ${new Date(r.createdAt).toLocaleString()}`
    : "";
  return `Revision ${r.revisionNumber} - ${who}${when}${
    r.isActive ? " (active)" : ""
  }`;
};

const statusVariant = (s: string): string =>
  s === "active" ? "success" : s === "draft" ? "secondary" : "dark";

const roleLabelBadge = (roleLabel: string): React.ReactNode => {
  if (roleLabel === "reply_target")
    return (
      <Badge bg="primary" className="ms-1">
        Reply target
      </Badge>
    );
  if (roleLabel === "signer")
    return (
      <Badge bg="dark" className="ms-1">
        Signer
      </Badge>
    );
  return null;
};

const PeopleRankingPromptCard = () => {
  // The people ranking prompt is per-role: landlord and tenant each have their
  // own editable persona + revision history, so a role toggle drives every read
  // and write. Switching role re-seeds every draft off the newly-loaded prompt.
  const [role, setRole] = useState<PeopleRole>("landlord");

  const prompt = usePeopleRankingPrompt(role);
  const {
    savePeoplePromptRuleMut,
    deletePeoplePromptRuleMut,
    reorderPeoplePromptRulesMut,
    savePeoplePromptPreambleMut,
    savePeoplePromptAddendumMut,
    restorePeoplePromptRevisionMut,
    testPeopleRankingPrompt,
  } = usePeopleDiscovery();

  const active = prompt.data?.active;
  const baseRevision = active?.revisionNumber ?? 1;
  const rules = useMemo(
    () =>
      [...(active?.addendum.rules ?? [])].sort((a, b) => a.position - b.position),
    [active?.addendum.rules]
  );

  // Any prompt write in flight. `baseRevision` only advances after the write's
  // refetch lands, so a second mutating click before then would carry a stale
  // base and 409. runWrite recovers, but disabling the controls avoids the
  // spurious "could not save" on an action the operator already confirmed.
  const writing =
    savePeoplePromptRuleMut.isLoading ||
    deletePeoplePromptRuleMut.isLoading ||
    reorderPeoplePromptRulesMut.isLoading ||
    savePeoplePromptPreambleMut.isLoading ||
    savePeoplePromptAddendumMut.isLoading ||
    restorePeoplePromptRevisionMut.isLoading;

  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Any write runs through here: on a stale-revision 409 (or any error) it shows
  // the message and refetches, so the card's baseRevision resyncs and the
  // operator can re-apply. On success it shows a short confirmation.
  const runWrite = async (fn: () => Promise<unknown>, ok: string) => {
    setError(null);
    setNote(null);
    try {
      await fn();
      setNote(ok);
    } catch (e) {
      setError((await extractAxiosErrorMessage(e)) ?? "Could not save the prompt.");
      await prompt.refetch();
    }
  };

  // --- Preamble -------------------------------------------------------------
  const [preambleDraft, setPreambleDraft] = useState<string>("");
  // Seed off the server preamble VALUE, not the revision number: a rule / reorder
  // / delete write bumps the revision but leaves the preamble unchanged, so keying
  // on the revision would reseed and silently discard an in-progress preamble edit.
  // Switching role also changes the server value, so this reseeds for the new role.
  // `null` marks "not yet seeded" so an empty server preamble still seeds once.
  const [seededPreamble, setSeededPreamble] = useState<string | null>(null);
  useEffect(() => {
    if (active && active.addendum.preamble !== seededPreamble) {
      setPreambleDraft(active.addendum.preamble);
      setSeededPreamble(active.addendum.preamble);
    }
  }, [active, seededPreamble]);
  const preambleChanged =
    active !== undefined &&
    preambleDraft.trim() !== active.addendum.preamble.trim();

  // --- Rule editing ---------------------------------------------------------
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [ruleDraft, setRuleDraft] = useState<RuleDraft>(emptyDraft());
  const [addingRule, setAddingRule] = useState<boolean>(false);

  const startEdit = (r: PromptRuleModel) => {
    setAddingRule(false);
    setEditingRuleId(r.rule_id);
    setRuleDraft(draftFromRule(r));
  };
  const startAdd = () => {
    setEditingRuleId(null);
    setAddingRule(true);
    setRuleDraft(emptyDraft());
  };
  const cancelRuleEdit = () => {
    setEditingRuleId(null);
    setAddingRule(false);
  };

  const saveRuleDraft = async () => {
    if (!ruleDraft.guidance.trim()) {
      setError("Guidance is required - it is the instruction itself.");
      return;
    }
    await runWrite(
      () =>
        savePeoplePromptRuleMut.mutateAsync({
          role,
          baseRevision,
          ruleId: editingRuleId ?? undefined,
          rule: {
            category: ruleDraft.category,
            title: ruleDraft.title,
            guidance: ruleDraft.guidance,
            why: ruleDraft.why,
            limits: ruleDraft.limits,
            evidence: ruleDraft.evidence,
            improvement: ruleDraft.improvement,
          },
          status: ruleDraft.status,
        }),
      editingRuleId ? `Saved ${editingRuleId}.` : "Added a new rule."
    );
    cancelRuleEdit();
  };

  const removeRule = async (ruleId: string) => {
    if (
      !window.confirm(
        `Delete rule ${ruleId}? (Recoverable by restoring a past revision.)`
      )
    )
      return;
    await runWrite(
      () => deletePeoplePromptRuleMut.mutateAsync({ role, baseRevision, ruleId }),
      `Deleted ${ruleId}.`
    );
    if (editingRuleId === ruleId) cancelRuleEdit();
  };

  const moveRule = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= rules.length) return;
    const order = rules.map((r) => r.rule_id);
    [order[index], order[target]] = [order[target], order[index]];
    await runWrite(
      () =>
        reorderPeoplePromptRulesMut.mutateAsync({
          role,
          baseRevision,
          ruleIds: order,
        }),
      "Reordered rules."
    );
  };

  const savePreamble = () =>
    runWrite(
      () =>
        savePeoplePromptPreambleMut.mutateAsync({
          role,
          baseRevision,
          preamble: preambleDraft,
        }),
      "Saved the preamble."
    );

  const resetToDefault = () => {
    if (!prompt.data) return;
    if (
      !window.confirm(
        "Reset the whole prompt to the shipped default? This appends a new revision (the current one stays in history)."
      )
    )
      return;
    void runWrite(
      () =>
        savePeoplePromptAddendumMut.mutateAsync({
          role,
          baseRevision,
          addendum: prompt.data!.defaultSeedAddendum,
          note: "Reset to shipped default",
        }),
      "Reset to the shipped default."
    );
  };

  // --- Revision history + diff ----------------------------------------------
  // Default the picker to the active revision (what's in view), re-seeding after a
  // write (or a role switch) bumps it. A manual pick sticks until the next write,
  // because baseRevision (the only dep) changes only when the active revision does.
  const [restoreOf, setRestoreOf] = useState<number>(0);
  useEffect(() => {
    setRestoreOf(baseRevision);
  }, [baseRevision]);
  const restore = () => {
    if (!restoreOf) return;
    void runWrite(
      () =>
        restorePeoplePromptRevisionMut.mutateAsync({
          role,
          baseRevision,
          revisionNumber: restoreOf,
        }),
      `Restored revision ${restoreOf}.`
    );
  };

  // Diff a chosen revision against the previous one in the (newest-first) history.
  const [diffTo, setDiffTo] = useState<number | null>(null);
  const diffFrom = useMemo(() => {
    if (diffTo === null || !prompt.data) return null;
    const lower = prompt.data.revisions
      .map((r) => r.revisionNumber)
      .filter((n) => n < diffTo)
      .sort((a, b) => b - a);
    return lower.length > 0 ? lower[0] : null;
  }, [diffTo, prompt.data]);
  const diffQuery = usePeopleRankingPromptDiff(
    role,
    diffFrom,
    diffTo,
    diffTo !== null
  );

  // --- Test panel -----------------------------------------------------------
  const [subjectKind, setSubjectKind] = useState<"voa" | "lead">("lead");
  const [subjectRef, setSubjectRef] = useState<string>("");
  const [testOp, setTestOp] = useState<{ key: string; siteId: string } | null>(
    null
  );
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestRunResult | null>(null);
  const [showTrace, setShowTrace] = useState<boolean>(false);

  const testOpQuery = useOperation(testOp?.key ?? null, testOp?.siteId ?? null);
  const testStatus = testOpQuery.data?.operation?.status;
  const testRunning =
    testOp !== null &&
    testStatus !== "completed" &&
    testStatus !== "failed" &&
    testStatus !== "cancelled";
  const progressMessage = testOpQuery.data?.operation?.progress?.message;

  useEffect(() => {
    if (!testOp || !testStatus) return;
    if (testStatus === "completed") {
      setTestResult((testOpQuery.data?.operation?.result as TestRunResult) ?? {});
      setTestOp(null);
    } else if (testStatus === "failed" || testStatus === "cancelled") {
      setTestError(
        testOpQuery.data?.operation?.error ?? "The test run did not complete."
      );
      setTestOp(null);
    }
  }, [testStatus, testOp, testOpQuery.data]);

  // Switching role invalidates any in-view test result / edit-in-progress for the
  // other role's prompt, so clear the transient panels (the prompt data + preamble
  // reseed themselves via their own effects on the new role's server values).
  useEffect(() => {
    cancelRuleEdit();
    // Drop any in-flight test op too: it belongs to the previous role, and if we
    // kept polling it its result would land + render under the newly-selected
    // role, misattributing that role's persona output.
    setTestOp(null);
    setTestResult(null);
    setTestError(null);
    setShowTrace(false);
    setError(null);
    setNote(null);
    setDiffTo(null);
     
  }, [role]);

  const handleTest = async () => {
    if (!active) return;
    setTestError(null);
    setTestResult(null);
    setShowTrace(false);
    try {
      const res = await testPeopleRankingPrompt.mutateAsync({
        role,
        // Test what a save of the current draft would run: the saved rules plus
        // any unsaved preamble edit.
        addendum: {
          preamble: preambleDraft,
          rules: active.addendum.rules,
          next_rule_seq: active.addendum.next_rule_seq,
        },
        subjectRef: subjectRef.trim(),
        subjectKind,
      });
      setTestOp({ key: res.operationKey, siteId: res.siteId });
    } catch (e) {
      setTestError(
        (await extractAxiosErrorMessage(e)) ?? "Failed to start the test run."
      );
    }
  };

  const traceQuery = usePeopleDiscoveryTrace(
    testResult?.traceSubjectRef ?? null,
    testResult?.traceRunId ?? null,
    showTrace
  );

  const testCandidates = testResult?.candidates ?? [];

  const renderRuleEditor = (idPrefix: string) => (
    <div
      style={{
        border: "1px solid #cfe2ff",
        borderRadius: 4,
        padding: 10,
        background: "#f8fbff",
      }}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>Title</Form.Label>
          <Form.Control
            size="sm"
            value={ruleDraft.title}
            placeholder="short label"
            onChange={(e) => setRuleDraft({ ...ruleDraft, title: e.target.value })}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>
            Category
          </Form.Label>
          <Form.Control
            size="sm"
            value={ruleDraft.category}
            placeholder="e.g. Seniority, Function, Signals"
            onChange={(e) =>
              setRuleDraft({ ...ruleDraft, category: e.target.value })
            }
          />
        </div>
        <div style={{ width: 130 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>
            Status
          </Form.Label>
          <Form.Select
            size="sm"
            value={ruleDraft.status}
            onChange={(e) =>
              setRuleDraft({
                ...ruleDraft,
                status: e.target.value as RuleDraft["status"],
              })
            }
          >
            <option value="active">active</option>
            <option value="draft">draft</option>
            <option value="retired">retired</option>
          </Form.Select>
        </div>
      </div>
      {RULE_TEXT_FIELDS.map((f) => (
        <div key={f.key} style={{ marginBottom: 6 }}>
          <Form.Label style={{ fontSize: 11, marginBottom: 2 }}>
            {f.label}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={f.rows}
            value={ruleDraft[f.key] as string}
            style={{ fontSize: 12 }}
            onChange={(e) =>
              setRuleDraft({ ...ruleDraft, [f.key]: e.target.value })
            }
          />
        </div>
      ))}
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          size="sm"
          variant="primary"
          disabled={!ruleDraft.guidance.trim() || writing}
          onClick={() => void saveRuleDraft()}
        >
          {savePeoplePromptRuleMut.isLoading ? "Saving..." : "Save rule"}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={cancelRuleEdit}>
          Cancel
        </Button>
        <span
          style={{ fontSize: 11, color: "#888", alignSelf: "center" }}
          id={`${idPrefix}-hint`}
        >
          Each save appends a new revision.
        </span>
      </div>
    </div>
  );

  return (
    <CollapsibleCard title="Contacts ranking prompt" initialOpen={false}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ fontSize: 12, color: "#888" }}>Editing the</span>
        <ButtonGroup size="sm">
          <Button
            variant={role === "landlord" ? "primary" : "outline-primary"}
            onClick={() => setRole("landlord")}
          >
            Landlord
          </Button>
          <Button
            variant={role === "tenant" ? "primary" : "outline-primary"}
            onClick={() => setRole("tenant")}
          >
            Tenant
          </Button>
        </ButtonGroup>
        <span style={{ fontSize: 12, color: "#888" }}>
          contacts-ranking persona
        </span>
      </div>

      {prompt.isLoading ? (
        <div style={{ fontSize: 13, color: "#888" }}>Loading prompt...</div>
      ) : prompt.isError || !prompt.data || !active ? (
        <Alert variant="danger">
          Could not load the {role} contacts-ranking prompt.
        </Alert>
      ) : (
        <div className="d-flex flex-column gap-3" style={{ fontSize: 13 }}>
          <div style={{ color: "#555" }}>
            The contacts-ranking pass reads the {role} company&apos;s people from
            Apollo and ranks them into bands (primary / also-strong /
            sustainability / other) using this editable persona: a{" "}
            <strong>preamble</strong> plus a set of addressable{" "}
            <strong>rules</strong> (each with why it exists, its limits and the
            evidence behind it). Every change appends a new revision; every
            discovered shortlist is stamped with the revision that produced it.
            The data-residency guardrail below is always appended and cannot be
            edited away.
          </div>

          <div style={{ fontSize: 12, color: "#888" }}>
            Active: {revisionLabel(active)} - {active.ruleCount} rule
            {active.ruleCount === 1 ? "" : "s"}, ~
            {Math.round(active.promptText.length / 4).toLocaleString()} tokens
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {note && <Alert variant="success">{note}</Alert>}

          {/* Preamble ------------------------------------------------------ */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <strong>Preamble</strong>
              {preambleChanged && (
                <span style={{ fontSize: 12, color: "#b8860b" }}>
                  Unsaved changes
                </span>
              )}
            </div>
            <Form.Control
              as="textarea"
              rows={12}
              value={preambleDraft}
              onChange={(e) => setPreambleDraft(e.target.value)}
              style={{ fontFamily: "monospace", fontSize: 12 }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <Button
                size="sm"
                variant="primary"
                disabled={!preambleChanged || writing}
                onClick={() => void savePreamble()}
              >
                {savePeoplePromptPreambleMut.isLoading
                  ? "Saving..."
                  : "Save preamble"}
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={writing}
                onClick={resetToDefault}
              >
                Reset whole prompt to shipped default
              </Button>
            </div>
          </div>

          {/* Rules --------------------------------------------------------- */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <strong>Rules</strong>
              <Button size="sm" variant="outline-primary" onClick={startAdd}>
                + Add rule
              </Button>
            </div>
            {addingRule && (
              <div style={{ marginBottom: 8 }}>{renderRuleEditor("add")}</div>
            )}
            {rules.length === 0 && !addingRule ? (
              <div style={{ fontSize: 12, color: "#666" }}>
                No rules on this revision - the whole prompt is in the preamble
                above. Use <strong>+ Add rule</strong> to break it out into
                addressable rules (each with its own why / limits / evidence), or{" "}
                <strong>Reset whole prompt to shipped default</strong> to adopt
                the shipped rule set.
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {rules.map((r, index) => (
                  <div
                    key={r.rule_id}
                    style={{
                      border: "1px solid #eee",
                      borderRadius: 4,
                      padding: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <strong>{r.rule_id}</strong>
                      {r.title && <span>{r.title}</span>}
                      <Badge bg={statusVariant(r.status)}>{r.status}</Badge>
                      {r.category && (
                        <span style={{ fontSize: 11, color: "#888" }}>
                          {r.category}
                        </span>
                      )}
                      <span style={{ flex: 1 }} />
                      <Button
                        size="sm"
                        variant="link"
                        style={{ fontSize: 11, padding: 0 }}
                        disabled={index === 0 || writing}
                        onClick={() => void moveRule(index, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        style={{ fontSize: 11, padding: 0 }}
                        disabled={index === rules.length - 1 || writing}
                        onClick={() => void moveRule(index, 1)}
                      >
                        ↓
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        style={{ fontSize: 11, padding: 0 }}
                        onClick={() =>
                          editingRuleId === r.rule_id
                            ? cancelRuleEdit()
                            : startEdit(r)
                        }
                      >
                        {editingRuleId === r.rule_id ? "Close" : "Edit"}
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        style={{ fontSize: 11, padding: 0, color: "#b02a37" }}
                        disabled={writing}
                        onClick={() => void removeRule(r.rule_id)}
                      >
                        Delete
                      </Button>
                    </div>
                    {editingRuleId === r.rule_id ? (
                      <div style={{ marginTop: 8 }}>
                        {renderRuleEditor(r.rule_id)}
                      </div>
                    ) : (
                      <div
                        style={{ fontSize: 12, color: "#444", marginTop: 4 }}
                      >
                        {r.guidance}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Enforced footer ---------------------------------------------- */}
          <div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
              Always appended (fixed, not editable) - data-residency guardrail:
            </div>
            <pre
              style={{
                background: "#f6f6f6",
                border: "1px solid #eee",
                borderRadius: 4,
                padding: 8,
                fontSize: 11,
                whiteSpace: "pre-wrap",
                margin: 0,
                color: "#555",
              }}
            >
              {prompt.data.enforcedFooter}
            </pre>
          </div>

          {/* Compiled preview --------------------------------------------- */}
          <CollapsibleCard
            title="Compiled prompt (what the ranker sees)"
            initialOpen={false}
          >
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontSize: 11,
                margin: 0,
                color: "#333",
                maxHeight: 400,
                overflow: "auto",
              }}
            >
              {active.promptText}
            </pre>
          </CollapsibleCard>

          {/* History + diff ----------------------------------------------- */}
          <div>
            <strong>Revision history</strong>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                marginTop: 4,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: 240 }}>
                <Form.Select
                  size="sm"
                  value={restoreOf || ""}
                  onChange={(e) => setRestoreOf(Number(e.target.value))}
                >
                  <option value="" disabled>
                    Select a revision...
                  </option>
                  {prompt.data.revisions.map((r) => (
                    <option key={r.revisionNumber} value={r.revisionNumber}>
                      {revisionLabel(r)}
                      {r.note ? ` - ${r.note}` : ""}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={
                  !restoreOf || restoreOf === active.revisionNumber || writing
                }
                onClick={restore}
              >
                Restore
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={!restoreOf}
                onClick={() => setDiffTo(restoreOf)}
              >
                View diff vs previous
              </Button>
            </div>
          </div>

          {/* Test panel --------------------------------------------------- */}
          <hr style={{ margin: "4px 0" }} />
          <div>
            <strong>Test this prompt</strong>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
              Runs the current draft (saved rules plus any unsaved preamble edit)
              against a real {role} for its chosen company. Nothing is saved and
              the row is not changed. Apollo discovery is free; the ranking pass
              costs a few cents.
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <div>
                <Form.Label style={{ fontSize: 12, marginBottom: 2 }}>
                  Subject
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={subjectKind}
                  onChange={(e) =>
                    setSubjectKind(e.target.value as "voa" | "lead")
                  }
                  style={{ width: 180 }}
                >
                  <option value="lead">Promoted lead id</option>
                  <option value="voa">VOA assessment ref</option>
                </Form.Select>
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <Form.Control
                  size="sm"
                  value={subjectRef}
                  placeholder={
                    subjectKind === "voa" ? "e.g. 30799525000" : "lead id (UUID)"
                  }
                  onChange={(e) => setSubjectRef(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                variant="outline-primary"
                disabled={
                  testRunning ||
                  !subjectRef.trim() ||
                  testPeopleRankingPrompt.isLoading
                }
                onClick={() => void handleTest()}
              >
                {testRunning ? (
                  <>
                    <Spinner as="span" size="sm" animation="border" />{" "}
                    {progressMessage ?? "Finding people..."}
                  </>
                ) : (
                  "Test run (free discovery)"
                )}
              </Button>
            </div>

            {testError && (
              <Alert variant="danger" style={{ marginTop: 8 }}>
                {testError}
              </Alert>
            )}

            {testResult && (
              <div style={{ marginTop: 10 }}>
                {testResult.status === "not_configured" ? (
                  <Alert variant="warning">
                    Apollo People Search is not configured in this environment,
                    so the test could not run.
                  </Alert>
                ) : testResult.status === "no_company" ? (
                  <Alert variant="warning">
                    No {role} company chosen for this row - pick one before
                    testing.
                  </Alert>
                ) : testResult.status === "quota_exhausted" ? (
                  <Alert variant="warning">
                    Apollo&apos;s daily discovery quota is used up - try again
                    tomorrow.
                  </Alert>
                ) : testResult.status &&
                  !["complete", "no_people"].includes(testResult.status) ? (
                  <Alert variant="danger">
                    The test run did not complete (status: {testResult.status}).
                    The subject row may have changed - try again.
                  </Alert>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <strong>
                        {testCandidates.length} candidate
                        {testCandidates.length === 1 ? "" : "s"}
                      </strong>
                      {testResult.companyName && (
                        <span style={{ fontSize: 12, color: "#888" }}>
                          from {testResult.apolloTotalEntries ?? 0} at{" "}
                          {testResult.companyName}
                          {testResult.sampled && " (sampled)"}
                        </span>
                      )}
                      {testResult.traceRunId && (
                        <Button
                          size="sm"
                          variant="link"
                          style={{ fontSize: 12, padding: 0 }}
                          onClick={() => setShowTrace(true)}
                        >
                          View AI trace
                        </Button>
                      )}
                    </div>
                    {testCandidates.length === 0 ? (
                      <div style={{ fontSize: 12, color: "#666" }}>
                        Apollo found the company but no matching people in the UK.
                        Open the AI trace to see what it checked.
                      </div>
                    ) : (
                      BAND_HEADINGS.map(({ band, label }) => {
                        const inBand = testCandidates.filter(
                          (c) => c.band === band
                        );
                        if (inBand.length === 0) return null;
                        return (
                          <div key={band} className="mt-2">
                            <div
                              className="text-uppercase text-muted"
                              style={{ fontSize: 11, letterSpacing: 0.5 }}
                            >
                              {label}
                            </div>
                            {inBand.map((person) => (
                              <div
                                key={person.apolloId}
                                style={{ fontSize: 12, padding: "2px 0" }}
                              >
                                <strong>
                                  {`${person.firstName} ${person.lastNameMasked}`.trim() ||
                                    "(no name)"}
                                </strong>{" "}
                                - {person.title}
                                {roleLabelBadge(person.roleLabel)}
                                {person.reasoning && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: 11 }}
                                  >
                                    {person.reasoning}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Diff modal --------------------------------------------------- */}
          <Modal
            show={diffTo !== null}
            onHide={() => setDiffTo(null)}
            size="lg"
            scrollable
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 18 }}>
                Diff: revision {diffFrom ?? "-"} → {diffTo}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {diffFrom === null ? (
                <div style={{ fontSize: 12, color: "#888" }}>
                  Revision {diffTo} is the earliest revision - nothing to diff
                  against.
                </div>
              ) : diffQuery.isLoading ? (
                <div style={{ fontSize: 12, color: "#888" }}>
                  Loading diff...
                </div>
              ) : diffQuery.isError || !diffQuery.data ? (
                <Alert variant="danger">Could not load the diff.</Alert>
              ) : (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <strong style={{ fontSize: 13 }}>Rule changes</strong>
                    {diffQuery.data.preambleChanged && (
                      <div style={{ fontSize: 12, color: "#0a58ca" }}>
                        Preamble edited
                      </div>
                    )}
                    {diffQuery.data.ruleChanges.length === 0 &&
                    !diffQuery.data.preambleChanged ? (
                      <div style={{ fontSize: 12, color: "#666" }}>
                        No changes.
                      </div>
                    ) : (
                      <ul style={{ fontSize: 12, marginBottom: 0 }}>
                        {diffQuery.data.ruleChanges.map((c, i) => (
                          <li key={`${c.rule_id}-${c.kind}-${i}`}>
                            <strong>{c.rule_id}</strong>
                            {c.title ? ` - ${c.title}` : ""}: {c.kind}
                            {c.detail ? ` (${c.detail})` : ""}
                            {c.fields_changed.length > 0
                              ? ` [${c.fields_changed.join(", ")}]`
                              : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <strong style={{ fontSize: 13 }}>Text diff</strong>
                    <pre
                      style={{
                        fontSize: 11,
                        whiteSpace: "pre-wrap",
                        margin: 0,
                        maxHeight: 320,
                        overflow: "auto",
                      }}
                    >
                      {diffQuery.data.textDiff.map((line, i) => (
                        <div
                          key={i}
                          style={{
                            color: line.startsWith("+")
                              ? "#0a7d24"
                              : line.startsWith("-")
                                ? "#b02a37"
                                : "#666",
                          }}
                        >
                          {line}
                        </div>
                      ))}
                    </pre>
                  </div>
                </>
              )}
            </Modal.Body>
          </Modal>

          <Modal
            show={showTrace}
            onHide={() => setShowTrace(false)}
            size="xl"
            scrollable
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 18 }}>
                Test run - AI trace ({role})
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {traceQuery.isLoading ? (
                <div style={{ fontSize: 12, color: "#888" }}>
                  Loading trace...
                </div>
              ) : traceQuery.data ? (
                <AITraceViewer trace={traceQuery.data} />
              ) : (
                <div style={{ fontSize: 12, color: "#888" }}>
                  No AI trace available for this run.
                </div>
              )}
            </Modal.Body>
          </Modal>
        </div>
      )}
    </CollapsibleCard>
  );
};

export default PeopleRankingPromptCard;
